/**
 * AI Management Dashboard
 *
 * Admin overview page with KPI stat cards and navigation cards
 * linking to Feature Flags, Workflow Presets, and Usage & Logs sub-pages.
 */

import {
  Container,
  Title,
  Text,
  Grid,
  Card,
  Group,
  ThemeIcon,
  Stack,
  Divider,
} from '@/components/ui';
import {
  IconToggleLeft,
  IconTemplate,
  IconChartBar,
  IconBrain,
  IconSettings,
  IconArrowRight,
  IconFlag,
  IconActivity,
} from '@tabler/icons-react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useAiFeatureFlagService, useAiWorkflowPresetService, useAiRequestLogService } from '@actschurch/ccms-sdk';

const sections = [
  {
    title: 'Feature Flags',
    description: 'Enable or disable AI features per tenant. Select which LLM model to use for each feature.',
    icon: IconToggleLeft,
    color: 'green',
    path: '/admin/ai/feature-flags',
  },
  {
    title: 'Workflow Presets',
    description: 'Manage system and custom workflow presets. Configure input types, target records, and prompts.',
    icon: IconTemplate,
    color: 'blue',
    path: '/admin/ai/presets',
  },
  {
    title: 'Usage & Logs',
    description: 'Monitor AI usage, token consumption, and request history across all tenants.',
    icon: IconChartBar,
    color: 'orange',
    path: '/admin/ai/usage',
  },
];

export default function AiManagementPage() {
  const navigate = useNavigate();
  const featureFlagService = useAiFeatureFlagService();
  const presetService = useAiWorkflowPresetService();
  const logService = useAiRequestLogService();

  // Fetch data for KPI cards
  const { data: flags = [] } = useQuery({
    queryKey: ['ai-feature-flags'],
    queryFn: () => featureFlagService.getAll(),
  });

  const { data: presets = [] } = useQuery({
    queryKey: ['ai-workflow-presets'],
    queryFn: () => presetService.getAll(),
  });

  const { data: recentLogs = [] } = useQuery({
    queryKey: ['ai-request-logs', 1, 1, 100],
    queryFn: () => logService.getByVendor(1, 1, 100),
  });

  const enabledFlags = flags.filter((f) => f.isEnabled).length;
  const uniqueVendors = new Set(flags.map((f) => f.vendorID)).size;

  return (
    <Container fluid py="xl">
      <Stack gap="lg">
        {/* Header */}
        <div>
          <Group gap="xs" mb={4}>
            <ThemeIcon size={36} radius="md" variant="light" color="violet">
              <IconBrain size={20} />
            </ThemeIcon>
            <div>
              <Title order={2}>AI Management</Title>
              <Text c="dimmed" size="sm">
                Configure AI features, workflow presets, and monitor usage.
              </Text>
            </div>
          </Group>
        </div>

        {/* KPI Stat Cards */}
        <Grid>
          <Grid.Col span={{ base: 6, md: 3 }}>
            <Card shadow="sm" padding="lg" radius="md" withBorder>
              <Group wrap="nowrap">
                <ThemeIcon size={48} radius="md" variant="light" color="green">
                  <IconFlag size={24} />
                </ThemeIcon>
                <div>
                  <Text size="xs" c="dimmed" tt="uppercase" fw={600}>
                    Features Enabled
                  </Text>
                  <Text size="xl" fw={700}>
                    {enabledFlags}
                  </Text>
                </div>
              </Group>
            </Card>
          </Grid.Col>
          <Grid.Col span={{ base: 6, md: 3 }}>
            <Card shadow="sm" padding="lg" radius="md" withBorder>
              <Group wrap="nowrap">
                <ThemeIcon size={48} radius="md" variant="light" color="blue">
                  <IconTemplate size={24} />
                </ThemeIcon>
                <div>
                  <Text size="xs" c="dimmed" tt="uppercase" fw={600}>
                    Total Presets
                  </Text>
                  <Text size="xl" fw={700}>
                    {presets.length}
                  </Text>
                </div>
              </Group>
            </Card>
          </Grid.Col>
          <Grid.Col span={{ base: 6, md: 3 }}>
            <Card shadow="sm" padding="lg" radius="md" withBorder>
              <Group wrap="nowrap">
                <ThemeIcon size={48} radius="md" variant="light" color="orange">
                  <IconActivity size={24} />
                </ThemeIcon>
                <div>
                  <Text size="xs" c="dimmed" tt="uppercase" fw={600}>
                    Recent Requests
                  </Text>
                  <Text size="xl" fw={700}>
                    {recentLogs.length}
                  </Text>
                </div>
              </Group>
            </Card>
          </Grid.Col>
          <Grid.Col span={{ base: 6, md: 3 }}>
            <Card shadow="sm" padding="lg" radius="md" withBorder>
              <Group wrap="nowrap">
                <ThemeIcon size={48} radius="md" variant="light" color="violet">
                  <IconSettings size={24} />
                </ThemeIcon>
                <div>
                  <Text size="xs" c="dimmed" tt="uppercase" fw={600}>
                    Active Vendors
                  </Text>
                  <Text size="xl" fw={700}>
                    {uniqueVendors}
                  </Text>
                </div>
              </Group>
            </Card>
          </Grid.Col>
        </Grid>

        {/* Section Navigation Cards */}
        <Grid>
          {sections.map((section) => (
            <Grid.Col key={section.title} span={{ base: 12, sm: 4 }}>
              <Card
                shadow="sm"
                padding="lg"
                radius="md"
                withBorder
                style={{ cursor: 'pointer' }}
                onClick={() => navigate(section.path)}
                h="100%"
              >
                <Group justify="space-between" mb="md">
                  <ThemeIcon size={48} radius="md" color={section.color} variant="light">
                    <section.icon size={24} />
                  </ThemeIcon>
                  <ThemeIcon size={32} radius="xl" variant="subtle" color="gray">
                    <IconArrowRight size={18} />
                  </ThemeIcon>
                </Group>
                <Divider mb="md" />
                <Text fw={600} mb={4}>
                  {section.title}
                </Text>
                <Text size="sm" c="dimmed">
                  {section.description}
                </Text>
              </Card>
            </Grid.Col>
          ))}
        </Grid>
      </Stack>
    </Container>
  );
}
